import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DynamicLoader from '@/atoms/dynamic-loader';
import MeetingRoomDetail from '@/organisms/meeting-room-landing/MeetingRoomDetail';
import RedirectToHome from '@/organisms/redirect-to-home';
import { getUnitsByLocationId } from '@core/booking/booking.service';
import { Unit } from '@core/booking/booking.types';
import { useLocationsStore } from '@core/locations/store/locations';
import { useSettingsStore } from '@core/settings/store/settings';

const MeetingRoomDetailPage = () => {
  const { t } = useTranslation();
  const { isMeetingRoomEnabled, textColor } = useSettingsStore();
  const { locations } = useLocationsStore();
  const router = useRouter();
  const { roomId, clientName, locationId } = router.query;

  const phpURL = useMemo(() => {
    return clientName ? `https://${clientName}.pickspace.com/api/app` : '';
  }, [clientName]);

  const [room, setRoom] = useState<Unit | null>(null);
  const [loading, setLoading] = useState(true);

  const location = useMemo(() => {
    if (!locationId || !locations.length) return null;
    return locations.find((loc) => loc.id === locationId) || null;
  }, [locationId, locations]);

  useEffect(() => {
    if (!isMeetingRoomEnabled && clientName) {
      router.push(`/home?clientName=${clientName}`);
    }
  }, [clientName, isMeetingRoomEnabled, router]);

  useEffect(() => {
    if (!phpURL || !locationId || !roomId) return;

    setLoading(true);
    getUnitsByLocationId(phpURL, locationId as string)
      .then((rooms: Unit[]) => {
        const found = Array.isArray(rooms) ? rooms.find((r) => r.id === roomId) : null;
        setRoom(found || null);
      })
      .catch(() => {
        setRoom(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [phpURL, locationId, roomId]);

  const handleBack = useCallback(() => {
    router.push(`/booking/meeting-room?clientName=${clientName}`);
  }, [router, clientName]);

  const handleBook = useCallback(() => {
    router.push(`/booking/meeting-room?clientName=${clientName}&locationId=${locationId}&roomId=${roomId}`);
  }, [router, clientName, locationId, roomId]);

  if (!isMeetingRoomEnabled) return <RedirectToHome message={t('booking.notAllowedMeetingRoom')} />;

  if (loading || !location) {
    return (
      <div className="flex flex-col items-center justify-center py-20 w-full">
        <DynamicLoader size="large" />
        <p className="mt-4 text-lg" style={{ color: textColor, opacity: 0.6 }}>
          {t('booking.loadingRoomDetails')}
        </p>
      </div>
    );
  }

  if (!room) {
    return (
      <div className="flex flex-col items-center justify-center py-20 w-full">
        <p className="text-lg" style={{ color: textColor, opacity: 0.6 }}>
          {t('booking.roomNotFound')}
        </p>
        <button
          type="button"
          onClick={handleBack}
          className="mt-4 px-6 py-2 rounded-lg text-white font-medium border-none cursor-pointer"
          style={{ backgroundColor: 'var(--main)' }}
        >
          {t('booking.backToRooms')}
        </button>
      </div>
    );
  }

  return <MeetingRoomDetail room={room} location={location} onBack={handleBack} onBook={handleBook} />;
};

export default MeetingRoomDetailPage;
